// src/scenes/ClassifyTechGameOverScene.js
import Phaser from 'phaser';
import BaseMenuScene from "./BaseMenuScene.js";
import MenuUIUtils from "../ui/MenuUIUtils.js";
import { auth, db, firebase } from '../firebaseConfig.js'; // Import firebase for Timestamp if needed by other logic
import sceneManager from "../utils/SceneManager.js";
import { hasSpecialAccess, getSpecialAccessUserId, getSpecialAccessDisplayName } from '../services/specialAccessService.js';

// Constants for Depths (can be simplified if needed)
const DEPTHS = {
    BACKGROUND: -10,
    PANEL_BACKGROUND: 5,
    ROW_BACKGROUND: 9,
    TEXT: 10,
    BUTTONS: 30,
};

export default class ClassifyTechGameOverScene extends BaseMenuScene {
    constructor() {
        super({ key: 'ClassifyTechGameOverScene' });
        this.score = 0;
        this.userId = '';
        this.playerDisplayName = 'Player';
        this.gameType = "ClassifyTech"; // Hardcoded for this scene
        this.topScores = []; // For storing fetched leaderboard scores
        this.playerRank = null; // Rank in the ClassifyTech leaderboard
        this.totalPlayers = 0; // Total players on the ClassifyTech leaderboard
        this.playerScoreEntry = null; // To hold the player's score entry if outside top N
        this.playerScoreOutsideTopTen = false; // Flag for the extra row
        this.leaderboardContainer = null; // Container for the leaderboard panel
        this.uiElements = {}; // Store UI elements for easy access/cleanup
        this.contentStartY = 100; // Default start Y for content panels
        this.padding = 20; // Inherited padding
    }

    init(data) {
        super.init(data); // Call BaseMenuScene init
        console.log(`[${this.scene.key}] Initializing with data:`, data);
        this.score = data.score || 0;
        this.gameType = "ClassifyTech"; // Ensure it's always set

        // Reset state variables
        this.topScores = [];
        this.playerRank = null;
        this.totalPlayers = 0;
        this.playerScoreEntry = null;
        this.playerScoreOutsideTopTen = false;
        this.leaderboardContainer = null;
        this.uiElements = {};

        // Determine Player Identity (same logic as other game over scenes)
        const user = auth.currentUser;
        const specialAccess = hasSpecialAccess();
        if (user) {
            this.userId = user.uid;
            this.playerDisplayName = data.playerDisplayName || user.displayName || user.email || 'Player';
            // Async fetch profile name to potentially update later
            db.collection("userProfiles").doc(user.uid).get().then(doc => {
                if(doc.exists && doc.data().displayName) {
                    this.playerDisplayName = doc.data().displayName;
                     // Potentially update UI if already rendered, e.g., this.updatePlayerRankDisplay();
                }
            }).catch(e => console.warn("Error fetching profile name in init:", e));
        } else if (specialAccess) {
            this.userId = getSpecialAccessUserId();
            this.playerDisplayName = getSpecialAccessDisplayName();
        } else {
            this.playerDisplayName = localStorage.getItem("tempSimpsonsName") || `Guest-${Math.floor(Math.random()*10000)}`;
            this.userId = `guest_${this.playerDisplayName}`;
             if (!localStorage.getItem("tempSimpsonsName")) {
                localStorage.setItem("tempSimpsonsName", this.playerDisplayName);
            }
        }
        console.log(`[${this.scene.key}] User: ${this.playerDisplayName} (ID: ${this.userId})`);
    }

    async create() {
        super.create(); // Creates background, sets up resize listener
        console.log(`[${this.scene.key}] Create START`);

        const width = this.cameras.main.width;
        const height = this.cameras.main.height;
        const centerX = width / 2;
        const headerHeight = Math.min(100, height * 0.12);
        this.contentStartY = headerHeight + this.padding;

        // --- Create Main Container ---
        this.leaderboardContainer = this.add.container(0, this.contentStartY).setDepth(DEPTHS.PANEL_BACKGROUND);
        this.uiElements.leaderboardContainer = this.leaderboardContainer;

        // --- Header Title ---
        this.uiElements.headerText = this.createThemedText(centerX, headerHeight * 0.5, "CLASSIFICATION COMPLETE", 'sizeXl', MenuUIUtils.COLORS.primary)
            .setDepth(DEPTHS.TEXT + 5);

        // --- Loading Text ---
        this.uiElements.loadingText = this.createThemedText(centerX, this.contentStartY + 50, "Loading results...", 'sizeLg', MenuUIUtils.COLORS.warning)
             .setDepth(DEPTHS.TEXT + 1);
        // Make sure loading text is added to a container or directly to the scene
        this.add.existing(this.uiElements.loadingText);

        // --- Load Scores and Build Layout ---
        try {
            await this.loadScoresAndLayout();
        } catch (error) {
             console.error(`[${this.scene.key}] Critical error loading scores/layout:`, error);
             if (this.uiElements.loadingText) this.uiElements.loadingText.destroy();
             this.fallbackDisplay("ERROR LOADING RESULTS"); // Show fallback UI
        }

        console.log(`[${this.scene.key}] Create FINISHED`);
    }

    async loadScoresAndLayout() {
        console.log(`[${this.scene.key}] loadScoresAndLayout START`);
        try {
            // Fetch scores specifically for ClassifyTech
            await this.fetchTopIndividualScores(10, "ClassifyTech", true); // Limit 10, type ClassifyTech, filter solo (redundant but clear)

            // Remove loading indicator
            if (this.uiElements.loadingText && this.uiElements.loadingText.active) {
                 this.uiElements.loadingText.destroy();
                 delete this.uiElements.loadingText;
            }

            // Create the actual UI layout
            this.createLayout();

             // Update dynamic text elements after layout is built
             this.updatePlayerRankDisplay(); // Update the rank/score text now

        } catch (error) {
            console.error(`[${this.scene.key}] Error during score loading or layout creation:`, error);
            if (this.uiElements.loadingText && this.uiElements.loadingText.active) {
                this.uiElements.loadingText.destroy();
                 delete this.uiElements.loadingText;
            }
            // Display error state within the container
            this.fallbackDisplay("ERROR LOADING DATA");
        }
        console.log(`[${this.scene.key}] loadScoresAndLayout FINISHED`);
    }

    createLayout() {
        if (!this.leaderboardContainer) {
             console.error(`[${this.scene.key}] leaderboardContainer missing, cannot create layout.`);
             return;
        }
        this.leaderboardContainer.removeAll(true); // Clear previous content (like loading text)

        const width = this.cameras.main.width;
        const centerX = width / 2;
        const panelWidth = Math.min(600, width * 0.85);
        let currentY = 0; // Y relative to leaderboardContainer (which starts at contentStartY)
        const panelPadding = 20;
        const sectionGap = 25;
        const tableBottomPadding = 15;

        // --- Main Panel Background (Sized at the end) ---
        const panelBg = this.add.rectangle(centerX, 0, panelWidth, 100, 0x112233, 0.85) // Slightly different BG color
            .setOrigin(0.5, 0).setStrokeStyle(2, 0x66CCFF, 0.8); // Use accent color
        this.leaderboardContainer.add(panelBg);
        this.uiElements.mainPanelBg = panelBg;

        // --- Player Score Display Box ---
        currentY += panelPadding;
        const scoreBoxHeight = 70;
        const scoreBoxY = currentY + scoreBoxHeight / 2;
        const scoreBoxWidth = Math.min(400, panelWidth * 0.8);

        // Score Box Background & Text (using helpers)
        const scoreBox = this.add.rectangle(centerX, scoreBoxY, scoreBoxWidth, scoreBoxHeight, 0x001844, 0.9)
             .setStrokeStyle(2, MenuUIUtils.COLORS.secondary, 1.0);
        this.leaderboardContainer.add(scoreBox);

        // Placeholders, will be updated by updatePlayerRankDisplay
        this.uiElements.scoreText = this.createThemedText(centerX, scoreBoxY - 10, `YOUR SCORE: ...`, 'sizeXl', '#00FFFF').setDepth(DEPTHS.TEXT);
        this.uiElements.rankText = this.createThemedText(centerX, scoreBoxY + 18, `GLOBAL RANK: #...`, 'sizeMd', '#FFFF66').setDepth(DEPTHS.TEXT);
        this.leaderboardContainer.add([this.uiElements.scoreText, this.uiElements.rankText]);
        currentY += scoreBoxHeight + sectionGap;

        // --- Leaderboard Table ---
        const tableWidth = panelWidth * 0.9;
        const listContainer = this.add.container(centerX, currentY); // Position container correctly
        this.leaderboardContainer.add(listContainer);
        this.uiElements.listContainer = listContainer;

        // Create the list and get its height
        const tableHeight = this.createAlignedScoreList(listContainer, 0, this.topScores, tableWidth, 10, true); // Pass container, use single player format
        currentY += tableHeight + tableBottomPadding + sectionGap; // Add gap below table/rank info

        // --- Return Button ---
        const buttonY = currentY; // Position below table content
        this.createReturnButton(buttonY); // Adds button to this.leaderboardContainer
        currentY += 60 + panelPadding; // Button height + final padding

        // --- Finalize Panel Size ---
        const finalPanelHeight = currentY;
        panelBg.setSize(panelWidth, finalPanelHeight);
        panelBg.setY(0); // Y is relative to container

        // Position the main container on the screen
        this.leaderboardContainer.setPosition(0, this.contentStartY);
    }

    // Update the score/rank display texts
    updatePlayerRankDisplay() {
        if (this.uiElements.scoreText && this.uiElements.scoreText.active) {
            this.uiElements.scoreText.setText(`YOUR SCORE: ${this.score.toLocaleString()}`);
        }
        if (this.uiElements.rankText && this.uiElements.rankText.active) {
            const rankString = (this.playerRank !== null && this.totalPlayers > 0)
                         ? `GLOBAL RANK: #${this.playerRank} / ${this.totalPlayers}`
                         : `GLOBAL RANK: #---`;
            this.uiElements.rankText.setText(rankString);
        }
    }

    // --- Score Fetching (Adapted from MultiplayerGameOverScene) ---
    async fetchTopIndividualScores(limit = 10, gameType = "ClassifyTech", filterSoloOnly = true) {
        const logPrefix = `[${this.scene.key} FetchScores]`;
        console.log(`${logPrefix} START - Game: ${gameType}, Limit: ${limit}`);
        try {
            this.topScores = [];
            this.playerRank = null;
            this.totalPlayers = 0;
            this.playerScoreEntry = null;
            this.playerScoreOutsideTopTen = false;

            if (!db || typeof db.collection !== 'function') throw new Error("Firestore connection error");
            if (!auth) throw new Error("Firebase Auth not initialized");

            const user = auth.currentUser;
            const specialAccess = hasSpecialAccess();
            let currentUserId = user ? user.uid : (specialAccess ? getSpecialAccessUserId() : null);
            let currentPlayerName = this.playerDisplayName; // Already determined in init

            // --- Fetch Top Scores ---
            let topScoresQuery = db.collection('scores').where('game', '==', gameType).orderBy('score', 'desc').limit(limit);
            const topScoresSnapshot = await topScoresQuery.get();
            let dbScoresData = [];
            topScoresSnapshot.forEach(doc => { dbScoresData.push({ id: doc.id, ...doc.data() }); });
            console.log(`${logPrefix} Fetched ${dbScoresData.length} top scores.`);

            // --- Calculate Player Rank ---
            if (currentUserId && typeof this.score === 'number' && this.score >= 0) {
                 try {
                     let rankQuery = db.collection('scores').where('game', '==', gameType).where('score', '>', this.score);
                     const higherScoresSnapshot = await rankQuery.get();
                     this.playerRank = higherScoresSnapshot.size + 1;
                     console.log(`${logPrefix} Calculated rank: #${this.playerRank}`);
                 } catch (rankError) { console.error(`${logPrefix} Error calculating rank:`, rankError); this.playerRank = null; }
            } else { this.playerRank = null; }

            // --- Fetch Total Player Count ---
            try {
                 let countQuery = db.collection('scores').where('game', '==', gameType);
                 const countSnapshot = await countQuery.get();
                 this.totalPlayers = countSnapshot.size;
                 console.log(`${logPrefix} Total players found: ${this.totalPlayers}`);
            } catch(countError) { console.error(`${logPrefix} Error fetching total player count:`, countError); this.totalPlayers = dbScoresData.length; }

            // --- Fetch Profiles (Simplified - assumes names are often stored) ---
            const userIdsToFetch = new Set();
            dbScoresData.forEach(data => { if (data?.userUID) userIdsToFetch.add(data.userUID); });
            if (currentUserId) userIdsToFetch.add(currentUserId); // Ensure current user's profile is considered
            let userProfiles = {};
            if (userIdsToFetch.size > 0) {
                // Simplified profile fetch for this example
                 console.log(`${logPrefix} Fetching profiles for ${userIdsToFetch.size} users...`);
                 // In a real app, batch this fetch properly
                 for (const uid of userIdsToFetch) {
                    try {
                         const profileDoc = await db.collection('userProfiles').doc(uid).get();
                         if (profileDoc.exists && profileDoc.data().displayName) {
                             userProfiles[uid] = { displayName: profileDoc.data().displayName };
                         }
                    } catch (profileFetchError) { console.warn(`${logPrefix} Error fetching profile ${uid}:`, profileFetchError); }
                 }
                 console.log(`${logPrefix} Fetched ${Object.keys(userProfiles).length} profiles.`);
            }

            // --- Format Scores for Display ---
            let processedScores = [];
            let currentRank = 1; let scoresAtRank = 1;
            for (let i = 0; i < dbScoresData.length; i++) {
                const data = dbScoresData[i];
                if (data && typeof data.score === 'number') {
                    if (i > 0 && data.score < dbScoresData[i-1].score) { currentRank += scoresAtRank; scoresAtRank = 1; }
                    else if (i > 0) { scoresAtRank++; }

                    const userId = data.userUID || 'unknown';
                    let displayName = userProfiles[userId]?.displayName || data.player || "Player"; // Use profile, stored, or default
                    if (userId === currentUserId && userProfiles[userId]?.displayName) {
                        displayName = userProfiles[userId].displayName; // Ensure current player uses latest profile name
                    }

                    const isScoreFromThisSession = (userId === currentUserId) && (data.score === this.score);

                    processedScores.push({
                        name: displayName, score: data.score, date: data.createdAt?.toDate() || new Date(),
                        userId: userId, highlight: isScoreFromThisSession, rank: currentRank, verified: data.verified === true
                    });
                }
            }

            // --- Handle Player Score Outside Top List ---
            const isInTopList = processedScores.some(s => s.highlight);
             const currentGameScoreData = { // Create the potential entry for the current player
                 name: currentPlayerName, score: this.score, date: new Date(),
                 userId: currentUserId, isCurrentGame: true, rank: this.playerRank || 0, verified: !!user
             };

            if (!isInTopList && this.playerRank !== null && this.playerRank > 0) { // Only add if rank calculated
                 this.playerScoreEntry = currentGameScoreData;
                 if (this.playerRank > limit) {
                     this.playerScoreOutsideTopTen = true;
                     console.log(`${logPrefix} Current score (#${this.playerRank}) outside top ${limit}, adding extra row.`);
                 } else {
                      this.playerScoreOutsideTopTen = false; // Rank is within top N, but score wasn't fetched (e.g. exact tie at cutoff)
                      console.log(`${logPrefix} Current score (#${this.playerRank}) within top ${limit}, but not in list. Will add entry.`);
                      // The createAlignedScoreList will handle inserting this if needed based on the flags
                 }
            } else {
                 this.playerScoreOutsideTopTen = false;
                 this.playerScoreEntry = null; // Don't add extra row if in top list or rank unknown
            }

            this.topScores = processedScores; // Assign the formatted top scores

            console.log(`${logPrefix} Fetch completed: ${this.topScores.length} scores for display, Rank: ${this.playerRank}, Total: ${this.totalPlayers}`);

        } catch (error) {
            console.error(`${logPrefix} CRITICAL ERROR:`, error);
            this.topScores = [{ rank: 1, name: "Error", score: 0, date: new Date(), userId:"error", verified: false, highlight: false }];
            this.playerRank = null; this.totalPlayers = 0; this.playerScoreOutsideTopTen = false; this.playerScoreEntry = null;
        }
    }

    // --- UI Helpers (Adapted or Reused) ---

    // Reusing createAlignedScoreList from MultiplayerGameOverScene
    createAlignedScoreList(container, startY, scores, tableWidth, maxRows, isSinglePlayerFormat = true) { // Defaulting to SP format
      if (!container || !container.scene || !container.scene.add || !container.active) {
          console.error("createAlignedScoreList: Invalid container or scene context."); return 0;
      }
      const scene = container.scene; container.removeAll(true);

      const rowHeight = 38; const headerHeight = 30; const cellPadding = 15;
      let currentY = startY;

      // Column Widths & X Positions (Using Single Player Format)
      const rankWidth = tableWidth * 0.12;
      const nameWidth = tableWidth * 0.38;
      const dateWidth = tableWidth * 0.20;
      const scoreWidth = tableWidth * 0.30;

      const rankX = -tableWidth / 2 + rankWidth / 2;
      const nameX = -tableWidth / 2 + rankWidth + cellPadding; // Left align name start
      const dateX = nameX + nameWidth + cellPadding + dateWidth / 2; // Center date column
      const scoreX = tableWidth / 2 - cellPadding; // Right align score edge

      // Table Header
      try {
          const headerBg = scene.add.rectangle(0, currentY + headerHeight / 2, tableWidth, headerHeight, 0x003366, 0.9).setOrigin(0.5);
          container.add(headerBg);
          const headerTextStyle = { fontSize: '14px', color: '#AAAAAA', fontStyle: 'bold' };
          container.add(scene.add.text(rankX, currentY + headerHeight / 2, "RANK", headerTextStyle).setOrigin(0.5));
          container.add(scene.add.text(nameX, currentY + headerHeight / 2, "PLAYER", headerTextStyle).setOrigin(0, 0.5));
          container.add(scene.add.text(dateX, currentY + headerHeight / 2, "DATE", headerTextStyle).setOrigin(0.5));
          container.add(scene.add.text(scoreX, currentY + headerHeight / 2, "SCORE", headerTextStyle).setOrigin(1, 0.5));
          currentY += headerHeight + 5;
      } catch(e) { console.error("Error creating header:", e); currentY += headerHeight + 5; }

      // Score Rows
      const displayScores = scores ? scores.slice(0, maxRows) : [];
      if (displayScores.length === 0) {
          try { container.add(scene.add.text(0, currentY + 20, "No scores available.", { fontSize: '16px', color: '#888888' }).setOrigin(0.5)); currentY += 40; }
          catch(e) { console.error("Error adding 'no scores' text:", e); }
      } else {
          displayScores.forEach((score, index) => {
              try {
                  const rowY = currentY + rowHeight / 2;
                  const isHighlighted = score.highlight === true;
                  let bgColor = index % 2 === 0 ? 0x101528 : 0x182038; let strokeColor = 0x203050; let strokeThickness = 1;
                  if (isHighlighted) { bgColor = 0x154560; strokeColor = MenuUIUtils.COLORS.secondary; strokeThickness = 1.5; }

                  const rowBg = scene.add.rectangle(0, rowY, tableWidth, rowHeight - 4, bgColor, 0.9).setStrokeStyle(strokeThickness, strokeColor, 0.8).setOrigin(0.5);
                  container.add(rowBg);

                  // Rank
                  let rankColor = isHighlighted ? MenuUIUtils.COLORS.secondary : '#FFFFFF';
                  if (index === 0 && !isHighlighted) rankColor = '#FFD700'; else if (index === 1 && !isHighlighted) rankColor = '#C0C0C0'; else if (index === 2 && !isHighlighted) rankColor = '#CD7F32';
                  container.add(scene.add.text(rankX, rowY, `${score.rank || index + 1}`, { fontSize: '16px', color: rankColor, fontStyle: 'bold' }).setOrigin(0.5));

                  // Player Name
                  let displayName = score.name || score.player || 'Player'; const nameLengthLimit = 18; if (displayName.length > nameLengthLimit) displayName = displayName.substring(0, nameLengthLimit - 1) + '…';
                  const nameTextStyle = { fontSize: '16px', color: isHighlighted ? MenuUIUtils.COLORS.secondary_light : '#FFFFFF', fontStyle: isHighlighted ? 'bold' : 'normal' };
                  const nameText = scene.add.text(nameX, rowY, displayName, nameTextStyle).setOrigin(0, 0.5);
                  container.add(nameText);

                  // Verified Star
                  if (score.verified) {
                      const starX = nameText.x + nameText.width + 5;
                      container.add(scene.add.text(starX, rowY, '★', { fontSize: '14px', color: MenuUIUtils.COLORS.primary_light }).setOrigin(0, 0.5));
                  }

                  // Date
                  let dateStr = '-'; if (score.date instanceof Date) { try { dateStr = score.date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); } catch (e) {} }
                  container.add(scene.add.text(dateX, rowY, dateStr, { fontSize: '14px', color: '#AAAAAA' }).setOrigin(0.5));

                  // Score
                  container.add(scene.add.text(scoreX, rowY, (score.score || 0).toLocaleString(), { fontSize: '16px', color: '#FFFFFF', fontStyle: 'bold' }).setOrigin(1, 0.5));

                  currentY += rowHeight;
              } catch (rowError) { console.error(`Error creating score row ${index}:`, rowError); }
          });
      }

      // Add player's score row if outside top list (SP Mode Only logic)
      if (this.playerScoreOutsideTopTen && this.playerScoreEntry) {
          try {
              currentY += 10; // Gap
              const separator = scene.add.rectangle(0, currentY, tableWidth * 0.8, 1, 0x444444, 0.7).setOrigin(0.5);
              container.add(separator);
              currentY += 15;

              const rowY = currentY + rowHeight / 2;
              const rowBg = scene.add.rectangle(0, rowY, tableWidth, rowHeight - 4, 0x154560, 0.9).setStrokeStyle(1.5, MenuUIUtils.COLORS.secondary, 0.8).setOrigin(0.5);
              container.add(rowBg);

              // Rank
              container.add(scene.add.text(rankX, rowY, `#${this.playerScoreEntry.rank}`, { fontSize: '16px', color: MenuUIUtils.COLORS.secondary, fontStyle: 'bold' }).setOrigin(0.5));

              // Player Name + Star
              let displayName = this.playerScoreEntry.name || this.playerDisplayName || 'Player';
              if (displayName.length > 18) displayName = displayName.substring(0, 15) + '…';
              const nameText = scene.add.text(nameX, rowY, displayName, { fontSize: '16px', color: MenuUIUtils.COLORS.secondary_light, fontStyle: 'bold' }).setOrigin(0, 0.5);
              container.add(nameText);
              if (this.playerScoreEntry.verified) {
                  const starText = scene.add.text(nameText.x + nameText.width + 5, rowY, '★', { fontSize: '14px', color: MenuUIUtils.COLORS.secondary_light }).setOrigin(0, 0.5);
                  container.add(starText);
              }

              // Date
              let dateStr = 'Now'; try { if(this.playerScoreEntry.date instanceof Date) dateStr = this.playerScoreEntry.date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); } catch(e){}
              container.add(scene.add.text(dateX, rowY, dateStr, { fontSize: '14px', color: '#AAAAAA' }).setOrigin(0.5));

              // Score
              container.add(scene.add.text(scoreX, rowY, (this.playerScoreEntry.score || 0).toLocaleString(), { fontSize: '16px', color: '#FFFFFF', fontStyle: 'bold' }).setOrigin(1, 0.5));

              currentY += rowHeight;
           } catch(extraRowError) { console.error("Error creating extra player score row:", extraRowError); }
       }

      return currentY - startY; // Return total height used by the list
    }

    createReturnButton(yPos) {
        if (!this.leaderboardContainer) { console.error("Cannot create return button: leaderboardContainer missing."); return; }
        const centerX = this.cameras.main.width / 2;
        const btnWidth = 280;
        const btnHeight = 60;

        // Use the helper from BaseMenuScene which adds to `this.uiElements`
        const returnBtn = this.createStyledButton(
            centerX, yPos,
            "RETURN TO MENU",
            () => this.returnToMainMenu(), // Callback uses 'this' instance
            MenuUIUtils.COLORS.success, // Green color for success/completion
            btnWidth, btnHeight, 'sizeLg'
        );

        if (returnBtn) {
             // Add to the leaderboard container
             this.leaderboardContainer.add(returnBtn); // Add button relative to the container

             // Animate button appearance (reuse animation logic)
             returnBtn.setAlpha(0).setScale(0.8);
             this.tweens?.add({ targets: returnBtn, alpha: 1, scale: 1, duration: 300, delay: 200, ease: 'Back.easeOut' });
        } else {
             console.error("Failed to create Return to Menu button.");
        }
    }

    returnToMainMenu() {
        console.log(`[${this.scene.key}] Returning to Main Menu...`);
        const user = auth.currentUser;
        const specialAccess = hasSpecialAccess();
        const isEffectivelyLoggedIn = (user && user.emailVerified) || specialAccess;
        
        // CRITICAL FIX: Use feature flag routing instead of hard-coded scene names
        const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
        const useDOMMenus = localStorage.getItem('pptHeroUseDOMMenus') === 'true';
        const useDOMAuthMenu = localStorage.getItem('pptHeroUseDOMAuthMenu') === 'true';
        
        let targetScene;
        if (useUniversalLanding) {
            targetScene = 'MainMenuSceneUniversal';
        } else if (isEffectivelyLoggedIn) {
            targetScene = useDOMAuthMenu ? "AuthenticatedMenuSceneDOM" : "AuthenticatedMenuScene";
        } else {
            // CRITICAL FIX: Always use DOM or Universal scenes
            const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
            targetScene = useUniversalLanding ? "MainMenuSceneUniversal" : "GuestMenuSceneDOM";
        }
        
        console.log(`ClassifyTechGameOverScene: Returning to menu - ${targetScene}`);
        sceneManager.switchToScene(targetScene);
    }

    // Simple fallback display
    fallbackDisplay(message) {
         if (this.leaderboardContainer) {
             this.leaderboardContainer.removeAll(true);
             const centerX = this.cameras.main.width / 2;
             // Display error message centered within the container's intended area
             const errorText = this.createThemedText(centerX, 50, message, 'sizeLg', MenuUIUtils.COLORS.danger);
             this.leaderboardContainer.add(errorText);
             this.createReturnButton(150); // Add fallback button below message
         }
     }

    handleResize() {
        super.handleResize(); // Handle background resize
        if (!this.cameras?.main || !this.leaderboardContainer?.active) return;

        const width = this.cameras.main.width;
        const height = this.cameras.main.height;
        const centerX = width / 2;
        const headerHeight = Math.min(100, height * 0.12);
        this.contentStartY = headerHeight + this.padding;

        // Reposition static elements
        this.uiElements.headerText?.setPosition(centerX, headerHeight * 0.5);

        // Reposition the main container
        this.leaderboardContainer.setPosition(0, this.contentStartY);

        // --- Recalculate Layout *Within* the Container ---
        // This is necessary if the content positioning depends heavily on width
        const panelWidth = Math.min(600, width * 0.85);
        let currentY = 0;
        const panelPadding = 20;
        const sectionGap = 25;
        const tableBottomPadding = 15;

        // Reposition Panel BG
        if (this.uiElements.mainPanelBg) {
             this.uiElements.mainPanelBg.x = centerX;
             // Height is recalculated/set at the end
        }

        // Reposition Score Box Elements
        currentY += panelPadding;
        const scoreBoxHeight = 70;
        const scoreBoxY = currentY + scoreBoxHeight / 2;
        // Find the score box background rectangle if stored, or reposition texts directly
        const scoreBoxBg = this.leaderboardContainer.list.find(item => item.type === 'Rectangle' && item !== this.uiElements.mainPanelBg); // Simple find logic
        if(scoreBoxBg) scoreBoxBg.setPosition(centerX, scoreBoxY);
        this.uiElements.scoreText?.setPosition(centerX, scoreBoxY - 10);
        this.uiElements.rankText?.setPosition(centerX, scoreBoxY + 18);
        currentY += scoreBoxHeight + sectionGap;

        // Reposition List Container
        if (this.uiElements.listContainer) {
             this.uiElements.listContainer.setPosition(centerX, currentY);
             // List content is relative, but we might need to resize the list's tableWidth
             const tableWidth = panelWidth * 0.9;
             // Recreate the list OR update column widths if performance is an issue
             // For simplicity, let's assume createAlignedScoreList handles internal positioning fine
             // If column widths need to change drastically, recreation might be better:
             // const tableHeight = this.createAlignedScoreList(this.uiElements.listContainer, 0, this.topScores, tableWidth, 10, true);
             // currentY += tableHeight + tableBottomPadding + sectionGap;
             // Note: Recreating the list might be visually jarring during resize.
             // A better approach would be to update text positions within the existing list.
             // However, the provided createAlignedScoreList redraws everything.
             // Let's just reposition the container for now.
             const listHeight = this.uiElements.listContainer.getBounds().height; // Estimate height
             currentY += listHeight + tableBottomPadding + sectionGap;
        } else {
             currentY += 200 + tableBottomPadding + sectionGap; // Estimate if container missing
        }

        // Reposition Return Button
        const buttonY = currentY;
        const returnBtn = this.uiElements.ReturnToMenuButton; // Assuming it's stored like this by createStyledButton
        if (returnBtn) {
             returnBtn.setPosition(centerX, buttonY);
             currentY += 60 + panelPadding; // Button height + final padding
        } else {
             currentY += 60 + panelPadding; // Estimate if button missing
        }

        // Resize Panel BG
        if (this.uiElements.mainPanelBg) {
             this.uiElements.mainPanelBg.setSize(panelWidth, currentY); // Set final height
             this.uiElements.mainPanelBg.setY(0); // Ensure Y is correct relative to container
        }
    }

    shutdown() {
        console.log(`[${this.scene.key}] Shutting down...`);
        // Destroy elements specific to this scene
        if (this.leaderboardContainer) {
             try { this.leaderboardContainer.destroy(true); } catch (e) {}
             this.leaderboardContainer = null;
        }
        // Clear references stored in uiElements (the objects themselves are destroyed by container.destroy)
        this.uiElements = {};
        // Reset state
        this.topScores = [];
        this.playerRank = null;
        this.totalPlayers = 0;
        this.playerScoreEntry = null;
        this.playerScoreOutsideTopTen = false;

        super.shutdown(); // Call parent shutdown LAST
        console.log(`[${this.scene.key}] Shutdown complete.`);
    }
}